import { apiBaseUrl } from '../apiConsts';
import { getDataWithRetry, mapPagedResult, sendWithoutBody } from '../fetchUtils';
import {
  isValidTemplateResponse,
  isValidTemplatesResponse,
} from '../apiValidators';

import { getDefaultError } from '../../utils';
import type {
  TrainingRoutineTemplate,
  WithOptionalId,
  PagedResponse,
} from '../../types';
import type { FetchResult } from '../fetchUtils';

export const createNewTemplate = async (
  template: WithOptionalId<TrainingRoutineTemplate>,
  trainerId: string,
): Promise<FetchResult<TrainingRoutineTemplate | null>> => {
  if (!trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Trainer id must be provided'),
    };
  }
  const createResponse = await getDataWithRetry<TrainingRoutineTemplate | null>(
    `${apiBaseUrl}training-routine-templates`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(template),
    },
    isValidTemplateResponse,
  );

  if (!createResponse.success || !createResponse.data) {
    return {
      success: false,
      data: null,
      error:
        createResponse.error ?? getDefaultError('Failed to create template'),
      status: createResponse.status,
      durationMs: createResponse.durationMs,
    };
  }

  const associateResult = await sendWithoutBody(
    `${apiBaseUrl}trainers/${trainerId}/associate/training-routine-template`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );
  if (!associateResult.success) {
    return associateResult;
  }
  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      trainerIds: [trainerId],
    },
  };
};

export const updateTemplateData = (
  template: TrainingRoutineTemplate,
): Promise<FetchResult<TrainingRoutineTemplate | null>> => {
  return getDataWithRetry(
    `${apiBaseUrl}training-routine-templates`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(template),
    },
    isValidTemplateResponse,
  );
};

export const deleteTemplateById = (
  templateId: string,
): Promise<FetchResult<null>> => {
  return sendWithoutBody(
    `${apiBaseUrl}training-routine-templates/${templateId}`,
    {
      method: 'DELETE',
    },
  );
};

export const fetchTrainerTemplates = async (
  trainerId: string,
): Promise<FetchResult<TrainingRoutineTemplate[]>> => {
  const response = await getDataWithRetry<PagedResponse<TrainingRoutineTemplate>>(
    `${apiBaseUrl}training-routine-templates/trainer/${trainerId}?page=0&size=1000&sort=name`,
    {},
    isValidTemplatesResponse,
  );
  return mapPagedResult(response);
};
